import React, { memo, useCallback } from "react";
function ProductTile({
  offer,
  matchedBankOffers,
  onClick,
  onVote,
  onRebateClick,
  onCashbackClick,
  banksLogos,
  setSelectedBankOffer,
}) {
  const isExpiringToday = (() => {
    if (!offer.endDttm?.toDate) return false;

    // Convert UTC to Central Time
    const endDateUTC = offer.endDttm.toDate();
    const options = { timeZone: "America/Chicago" };
    const endDateCT = new Date(
      endDateUTC.toLocaleString("en-US", options)
    );

    const today = new Date();
    const todayCT = new Date(
      today.toLocaleString("en-US", options)
    );

    todayCT.setHours(0, 0, 0, 0);
    endDateCT.setHours(0, 0, 0, 0);

    return endDateCT.getTime() === todayCT.getTime();
  })();

  // --- New: Upcoming offer ---
  const isUpcoming = (() => {
    if (!offer.startDttm?.toDate) return false;

    const startDateUTC = offer.startDttm.toDate();
    const options = { timeZone: "America/Chicago" };
    const startDateCT = new Date(startDateUTC.toLocaleString("en-US", options));

    const today = new Date();
    const todayCT = new Date(today.toLocaleString("en-US", options));
    todayCT.setHours(0, 0, 0, 0);
    startDateCT.setHours(0, 0, 0, 0);

    return startDateCT.getTime() > todayCT.getTime();
  })();


  const handleRebateClick = useCallback(
    (e) => {
      e.stopPropagation();
      onRebateClick(offer);
    },
    [onRebateClick, offer]
  );

  const handleCashbackClick = useCallback(
    (e) => {
      e.stopPropagation();
      onCashbackClick(offer);
    },
    [onCashbackClick, offer]
  );

  return (
    <div
      onClick={onClick}
      className="border rounded-lg p-2 shadow hover:shadow-lg transition relative flex flex-col cursor-pointer"
      title="Click to view offer details"
    >
      {offer.cashbackAmount > 0 && (
        <button
          onClick={handleCashbackClick}
          className="bg-green-100 px-1 py-0.5 rounded text-green-700 font-bold animate-pulse text-xs shadow"
          type="button"
        >
          Cashback Offer
        </button>
      )}

      {offer.offerImage && (
        <div style={{ width: "20vw", maxWidth: 80, height: 80 }}>
          <img
            src={offer.offerImage}
            alt={offer.productName || "Offer Image"}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
      )}

      <h3 className="font-semibold text-xs sm:text-sm mb-0.5 truncate">{offer.productName}</h3>
      <p className="mb-0.5 text-red-500 truncate text-xs sm:text-sm font-bold">
        {offer.vendor}
      </p>

      <div className="mb-0.5 text-indigo-600 font-bold text-sm sm:text-base">
        {!isNaN(Number(offer.offerPrice)) && offer.offerPrice !== "" && (
          <div className="mb-0.5 text-indigo-600 font-bold text-base sm:text-lg">
            ${Number(offer.offerPrice).toFixed(2)}
            {!isNaN(Number(offer.originalPrice)) &&
              Number(offer.originalPrice) > 0 &&
              offer.originalPrice !== "" && (
                <span className="ml-2 line-through text-gray-500 text-sm sm:text-base font-semibold">
                  ${Number(offer.originalPrice).toFixed(2)}
                </span>
              )}
          </div>
        )}
      </div>
      {(offer.category || (Array.isArray(offer.tags) && offer.tags.length > 0)) && (
        <p className="mb-1 text-xs text-gray-600">
          {offer.category && <>Category: {offer.category}<br /></>}
          {Array.isArray(offer.tags) && offer.tags.length > 0 && (
            <>Tags: {offer.tags.join(", ")}</>
          )}
        </p>
      )}
      {offer?.freeShippingMinimum === 0 ? (
        <p className="mb-1 text-blue-600 font-semibold text-xs">
          🚚 Free Shipping
        </p>
      ) : offer.freeStorePickup?.toLowerCase() === "yes" ? (
        <p className="mb-1 text-green-600 font-semibold text-xs">
          🏪 Free Store Pickup
        </p>
      ) : null}

      {offer.offerType && (
        <p className="text-sm text-rose-700 font-bold mb-2 select-none flex items-center gap-1">
          {offer.offerType}
          {offer.rebateAmount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRebateClick(e);
              }}
              type="button"
              aria-label="Rebate Offer Info"
              className="text-red-700 font-bold text-base hover:text-red-900"
            >
              ℹ️
            </button>
          )}
        </p>
      )}

      {isExpiringToday && (
        <p className="text-xs text-white bg-red-600/70 px-1.5 py-0.5 rounded absolute top-2 left-2 font-semibold shadow">
          ⚠️ Expiring Today
        </p>
      )}

      {isUpcoming && (
        <p className="text-xs text-white bg-yellow-500/80 px-1.5 py-0.5 rounded absolute top-2 left-2 font-semibold shadow">
          ⏳ Upcoming Offer
        </p>
      )}

      {matchedBankOffers?.length > 0 && (
        <div className="absolute top-1 right-1 flex flex-col items-end gap-1.5 z-10">
          {matchedBankOffers.map((b, i) => {
            const bankKey = b.bankName?.toLowerCase();
            const iconSrc = bankKey && banksLogos[bankKey] ? banksLogos[bankKey] : b.icon;
            return (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBankOffer(b);
                }}
                className="rounded-full border border-gray-300"
                style={{
                  width: "2.25rem", // 36px
                  height: "2.25rem",
                  animation: "flashHighlight 2s infinite",
                  overflow: "hidden",
                }}
                title={`Matched Bank Offer - ${b.bankName}`}
                type="button"
              >
                <img
                  src={iconSrc}
                  alt={b.bankName}
                  className="w-full h-full object-contain rounded-full"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default memo(ProductTile);
